import {
  PortaleDto,
  Role,
  UserInternoDto,
  getGetClientiQueryKey,
  getGetUsersQueryKey,
  useDeleteUser,
  useGetPortali,
  useGetUsers,
  useSignInAsUser,
} from "@/api";
import { useAuth } from "@/context/useAuth";
import Box from "@/elements/Box";
import Button from "@/elements/Button";
import Table from "@/elements/Table";
import Typography from "@/elements/Typography";
import { getRoleColor } from "@/utils/rolesAndPermissions";
import { useConfirmationDialog } from "@/utils/useConfirmationDialog";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import SearchIcon from "@mui/icons-material/Search";
import { Chip, IconButton, TextField } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { createColumnHelper } from "@tanstack/react-table";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import Loading from "../Loading";
import LoadingError from "../LoadingError";
import PortaleChip from "../PortaleChip";
import UtenteDialog from "../UtenteDialog";
import { uniq } from "lodash";

const columnHelper = createColumnHelper<UserInternoDto>();

function UtentiPage() {
  const { hasPermission, user } = useAuth();
  const { t } = useTranslation();

  const { data: portaliData } = useGetPortali();

  const { isLoading, error, data } = useGetUsers();

  const [filtro, setFiltro] = useState("");

  const [userDialogState, setUserDialogState] = useState({
    open: false,
    user: null as UserInternoDto | null,
  });

  const { askConfirmation, confirmationDialog } = useConfirmationDialog();

  const { mutateAsync } = useDeleteUser({
    mutation: {
      onSuccess: () => {
        toast.success(t("utentiPage.toast.eliminato"));
      },
      onError: () => {
        toast.error(t("errori.richiesta"));
      },
    },
  });

  const queryClient = useQueryClient();

  const { mutateAsync: impersonateAsync } = useSignInAsUser();
  const impersonaUtente = useCallback((id: number) => {
    impersonateAsync({ userIdToImpersonate: id }).then(() => {
      window.location.href = "/aziende";
      setTimeout(() => {
        window.location.reload();
      }, 10);
    });
  }, []);

  const deleteUser = useCallback(
    async (userId: number) => {
      try {
        await mutateAsync({ id: userId });
        queryClient.invalidateQueries({ queryKey: getGetUsersQueryKey() });
        queryClient.invalidateQueries({ queryKey: getGetClientiQueryKey() });
      } catch (err) {}
    },
    [mutateAsync, queryClient]
  );

  const columns = useMemo(() => {
    const columns = [
      columnHelper.accessor("name", {
        header: t("utentiPage.colonne.nome"),
        size: undefined,
      }),
      columnHelper.accessor("lastName", {
        header: t("utentiPage.colonne.cognome"),
        size: undefined,
      }),
      columnHelper.accessor("email", {
        header: t("utentiPage.colonne.email"),
        size: undefined,
      }),
      columnHelper.accessor(
        (x) =>
          x.userRoles
            ?.map(
              (x) =>
                portaliData?.data?.find((p) => p.id === x.portaleId)?.nome ??
                null
            )
            .filter((x) => !!x)
            .join(", "),
        {
          header: t("utentiPage.colonne.portali"),
          size: undefined,
          cell: ({ row }) => {
            const portali = (uniq(
              row.original.userRoles?.map((r) => r.portaleId) ?? []
            )
              ?.map((pId) => portaliData?.data?.find((p) => p.id === pId))
              .filter((x) => !!x) ?? []) as PortaleDto[];

            return (
              <Box display="flex" gap={0.5}>
                {portali
                  .sort((a, b) => a.id - b.id)
                  .map((p) => {
                    return <PortaleChip key={p.id} portale={p.id} />;
                  })}
              </Box>
            );
          },
        }
      ),
      columnHelper.accessor("userRoles", {
        header: t("utentiPage.colonne.ruoli"),
        size: undefined,
        cell: ({ row }) => {
          const userRoles = uniq(
            row.original.userRoles?.map((x) => x.role) ?? []
          );

          return (
            <Box display="flex" gap={0.5} flexWrap="wrap">
              {userRoles.map((role) => {
                const [bg, fg] = getRoleColor(role as Role);
                return (
                  <Chip
                    key={role}
                    label={role}
                    size="small"
                    sx={{
                      backgroundColor: bg,
                      color: fg,
                    }}
                  />
                );
              })}
            </Box>
          );
        },
      }),
      hasPermission("UsersInterniChange") &&
        columnHelper.display({
          id: "buttons",
          header: "",
          size: 250,
          maxSize: 250,
          cell: ({ row }) => {
            return (
              <Box display="flex" gap={0.5} flexWrap="wrap">
                {user?.email === "admin@stark" && (
                  <Button
                    variant="outlined"
                    size="small"
                    color="primary"
                    onClick={() => {
                      impersonaUtente(row.original.id!);
                    }}
                  >
                    {t("utentiPage.azioni.impersona")}
                  </Button>
                )}
                <IconButton
                  title={t("azioni.modifica")}
                  size="small"
                  color="primary"
                  onClick={() => {
                    setUserDialogState({
                      open: true,
                      user: row.original,
                    });
                  }}
                >
                  <EditIcon />
                </IconButton>
                <IconButton
                  title={t("azioni.elimina")}
                  size="small"
                  color="error"
                  disabled={
                    row.original.id === 1 // main user: non eliminabile
                  }
                  onClick={() => {
                    askConfirmation({
                      title: t("utentiPage.confermaElimina.titolo"),
                      message: t("utentiPage.confermaElimina.messaggio", {
                        nome: row.original.name,
                        cognome: row.original.lastName,
                        email: row.original.email,
                      }),
                      okColor: "error",
                      onConfirm: () => {
                        return deleteUser(row.original.id!);
                      },
                    });
                  }}
                >
                  <DeleteIcon />
                </IconButton>
              </Box>
            );
          },
        }),
    ].filter((x) => x);
    return columns as unknown as Exclude<(typeof columns)[number], boolean>[];
  }, [
    setUserDialogState,
    askConfirmation,
    deleteUser,
    hasPermission,
    portaliData,
    user,
    t,
  ]);

  if (isLoading) {
    return <Loading />;
  }
  if (error) {
    return <LoadingError error={error} />;
  }

  const utenti = data.data || [];

  return (
    <Box p={2}>
      <Box
        display="flex"
        flexWrap="wrap"
        alignItems="center"
        justifyContent="space-between"
      >
        <Typography variant="h4" mr={2}>
          {t("utentiPage.titolo")}
        </Typography>
        <Box display="flex" gap={1} pt={1} flexWrap="wrap">
          {hasPermission("UsersInterniChange") && (
            <Button
              size="small"
              color="light"
              onClick={() => {
                setUserDialogState({
                  open: true,
                  user: null,
                });
              }}
            >
              {t("utentiPage.azioni.aggiungi")}
            </Button>
          )}
          <TextField
            placeholder={t("azioni.filtra")}
            value={filtro}
            onChange={(e) => setFiltro(e.target.value)}
            InputProps={{ startAdornment: <SearchIcon sx={{ ml: 1.5 }} /> }}
          />
        </Box>
      </Box>

      <Table
        columns={columns}
        data={utenti}
        sortable={true}
        initialSort={[{ id: "email", desc: false }]}
        globalFilter={filtro}
      />

      <UtenteDialog
        open={userDialogState.open}
        user={userDialogState.user}
        onClose={() => {
          setUserDialogState((x) => ({
            ...x,
            open: false,
          }));
        }}
      />
      {confirmationDialog}
    </Box>
  );
}

export default UtentiPage;
